import { NibiruTxClient, NibiruQuerier } from "@nibiruchain/nibijs";
import {
  showTransactionToast,
  showTxSuccess,
  showTxError,
} from "../Toast/TransactionToast";
import { setPerpsUpdate } from "../../ReduxSlices/TradeSlice";
import { evmToBech32Address } from "../../evm/services/EvmContract";
import { getNibiruSDK, getContractAddresses, getCosmosExplorerUrl } from "../../config/network";
import { enforceClientOrigin } from "../../utils/clientOrigin";

const nibiru = getNibiruSDK();

/**
 * Fetch perpetual borrowing fees from GraphQL API
 * @param {number} marketId - Market ID 
 * @param {number} collateralId - Collateral ID
 * @returns {Promise<Object>} Object with openFeePct, closeFeePct, triggerOrderFeePct
 */
export const getPerpBorrowingFees = async (marketId, collateralId) => {
  try {
    console.log("[getPerpBorrowingFees] Fetching borrowing data:", { marketId, collateralId });

    const url = "https://sai-keeper.nibiru.fi/query";
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({
        query: `
          query GetPerpBorrowing($marketId: Int!, $collateralId: Int!) {
            perp {
              borrowing(marketId: $marketId, collateralId: $collateralId) {
                openFeePct
                closeFeePct
                triggerOrderFeePct
                feesPerHourLong
                feesPerHourShort
                oiLong
                oiShort
                oiMax
                maxLeverage
                minLeverage
                minPositionSizeUSD
              }
            }
          }
        `,
        variables: { marketId, collateralId },
      }),
    });

    console.log("[getPerpBorrowingFees] GraphQL response status:", response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    console.log("[getPerpBorrowingFees] GraphQL raw response:", json);

    if (json.errors) {
      console.error("[getPerpBorrowingFees] GraphQL errors:", json.errors);
      throw new Error("Failed to fetch fee percentages.");
    }

    if (!json.data || !json.data.perp || !json.data.perp.borrowing) {
      console.error("[getPerpBorrowingFees] Invalid response structure:", json);
      throw new Error("Invalid response structure from GraphQL");
    }

    const borrowingData = json.data.perp.borrowing;
    console.log("[getPerpBorrowingFees] Borrowing data:", borrowingData);
    console.log("[getPerpBorrowingFees] Funding rates - Long:", borrowingData.feesPerHourLong, "Short:", borrowingData.feesPerHourShort);
    console.log("[getPerpBorrowingFees] OI - Long:", borrowingData.oiLong, "Short:", borrowingData.oiShort, "Max:", borrowingData.oiMax);
    return borrowingData;
  } catch (error) {
    console.error(`[getPerpBorrowingFees] Error for market ${marketId}, collateral ${collateralId}:`, error);
    return null;
  }
};

/**
 * Calculate opening fee based on collateral amount, leverage, and fee percentages
 * @param {number} collateralAmount - Collateral amount in USDC
 * @param {number} leverage - Leverage multiplier
 * @param {boolean} isLimitOrder - Whether this is a limit order (affects fee)
 * @param {Object} borrowingFees - Fees object from getPerpBorrowingFees
 * @returns {Object} Object with feeAmount and feePercentage
 */
export const calculateOpeningFee = (collateralAmount, leverage, isLimitOrder, borrowingFees) => {
  if (!borrowingFees || !collateralAmount || !leverage) {
    return { feeAmount: 0, feePercentage: 0 };
  }

  const positionSize = collateralAmount * leverage;
  
  // Use appropriate fee percentage based on order type
  const feePercentage = isLimitOrder ? borrowingFees.triggerOrderFeePct : borrowingFees.openFeePct;
  
  // Calculate fee based on position size
  const feeAmount = positionSize * feePercentage;
  
  return {
    feeAmount: feeAmount,
    feePercentage: feePercentage * 100 // Convert to percentage for display
  };
};

/**
 * Calculate closing fee based on collateral amount, leverage, and fee percentages
 * @param {number} collateralAmount - Collateral amount in USDC
 * @param {number} leverage - Leverage multiplier
 * @param {Object} borrowingFees - Fees object from getPerpBorrowingFees
 * @returns {Object} Object with totalFee and feePercentage
 */
export const calculateClosingFee = (collateralAmount, leverage, borrowingFees) => {
  if (!borrowingFees || !collateralAmount || !leverage) {
    return { totalFee: 0, feePercentage: 0 };
  }

  const positionSize = collateralAmount * leverage;
  
  // Use closing fee percentage
  const feePercentage = borrowingFees.closeFeePct;
  
  // Calculate fee based on position size
  const totalFee = positionSize * feePercentage;
  
  return {
    totalFee: totalFee,
    feePercentage: feePercentage * 100 // Convert to percentage for display
  };
};

/**
 * Execute perpetuals trading contract
 * @param {string} chainId - Chain ID
 * @param {string} endpt - Endpoint
 * @param {string} contractAddr - Contract address
 * @param {Object} dispatch - Redux dispatch
 * @param {Object} CONTRACT_MESSAGE - Contract message
 * @param {string} account - User account
 * @param {string} walletEx - Wallet extension name
 * @param {Array} tokenToSend - Tokens to send with transaction
 * @returns {Promise<string|boolean>} Transaction hash or false on failure
 */
export const executePerps = async (
  chainId,
  endpt,
  contractAddr,
  dispatch,
  CONTRACT_MESSAGE,
  account,
  walletEx,
  tokenToSend
) => {
  try {
    // Mandatory: guarantee client_origin on every open_trade message, no matter
    // how the caller built it. No-op for non-open_trade messages.
    enforceClientOrigin(CONTRACT_MESSAGE);

    showTransactionToast("Placing order, just a second...");
    await window[walletEx].enable(chainId);
    const signer = await window[walletEx].getOfflineSigner(chainId);
    const signingClient = await NibiruTxClient.connectWithSigner(endpt, signer);
    let hash;

    if (tokenToSend) {
      const tx = await signingClient.wasmClient.executeMultiple(
        account,
        [
          {
            contractAddress: contractAddr,
            msg: CONTRACT_MESSAGE,
            funds: [
              {
                amount: tokenToSend[0].amount,
                denom: tokenToSend[0].denom,
              },
            ],
          },
        ],
        "auto",
        "perp_trade"
      );
      hash = tx.transactionHash;
    } else {
      const tx = await signingClient.wasmClient.execute(
        account,
        contractAddr,
        CONTRACT_MESSAGE,
        "auto",
        "",
        []
      );
      hash = tx.transactionHash;
    }

    console.log("Transaction successful! Hash:", hash);

    // Show transaction hash in success message with clickable link
    const explorerUrl = getCosmosExplorerUrl(hash);

    showTxSuccess(
      <div>
        Transaction confirmed!
        <br />
        <a
          href={explorerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline hover:text-blue-700"
        >
          View on Explorer
        </a>
      </div>
    );
    
    dispatch(setPerpsUpdate());
    
    // SMART EVENT EMISSION: Only trigger events for actual blockchain changes
    setTimeout(() => {
      // Emit user action event immediately
      window.dispatchEvent(new CustomEvent('userTradeAction', {
        detail: { 
          txHash: hash,
          contractMessage: CONTRACT_MESSAGE,
          account,
          timestamp: Date.now()
        }
      }));
      
      console.log('🔄 User trade action event emitted');
    }, 1000);

    // DELAYED STATE CHECK: Check for blockchain state changes after settlement
    setTimeout(() => {
      // This will trigger the smart detector to check for state changes
      window.dispatchEvent(new CustomEvent('blockchainStateCheck', {
        detail: { account, timestamp: Date.now() }
      }));
      
      console.log('Blockchain state check triggered');
    }, 5000);
    
    return hash;
  } catch (error) {
    console.error("Transaction failed:", error);

    const errorMessage = error.toString().toLowerCase();
    let userMessage = "Unable to process transaction. Please try again.";

    if (
      errorMessage.includes("querier contract error") ||
      errorMessage.includes("generic error")) {
      userMessage = "USDC Collateral Price is expired. Please try again later.";
    } else if (
      errorMessage.includes("insufficient funds") ||
      errorMessage.includes("out of gas")
    ) {
      userMessage = "Insufficient balance to complete this transaction. Please add funds to your wallet.";
    } else if (
      errorMessage.includes("account sequence mismatch") ||
      errorMessage.includes("sequence")
    ) {
      userMessage = "Transaction sequence error. Please try again in a moment.";
    } else if (
      errorMessage.includes("chain id") ||
      errorMessage.includes("network")
    ) {
      userMessage = "Network connection issue. Please check your wallet is connected to the correct network.";
    } else if (
      errorMessage.includes("rejected") ||
      errorMessage.includes("declined") ||
      errorMessage.includes("cancelled")
    ) {
      userMessage = "Transaction was declined. Please try again.";
    } else if (
      errorMessage.includes("timeout") ||
      errorMessage.includes("timed out")
    ) {
      userMessage = "Transaction timed out. The network might be congested, please try again.";
    } else if (
      errorMessage.includes("not connected") ||
      errorMessage.includes("no wallet")
    ) {
      userMessage = "Wallet connection lost. Please reconnect your wallet and try again.";
    } else if (errorMessage.includes("slippage")) {
      userMessage = "Price changed during transaction. Try again with a higher slippage tolerance.";
    } else if (errorMessage.includes("open trigger not hit") || errorMessage.includes("invalid trigger price")) {
      userMessage = "Your limit order hasn't reached the trigger price yet. Please try again later.";
    } else if (errorMessage.includes("limit") || errorMessage.includes("max")) {
      userMessage = "This transaction exceeds platform limits. Please try a smaller amount.";
    } else if (errorMessage.includes("is less than the total fees required")) {
      userMessage = "Collateral amount is less than the Borrowing fees required.";
    }

    showTxError(userMessage);
    console.error(`Transaction error details: ${error.toString()}`);
    return false;
  }
};

/**
 * Query a smart contract
 * @param {string} contractAddr - Contract address
 * @param {Object} CONTRACT_MESSAGE - Query message
 * @param {string} endpt - Endpoint
 * @returns {Promise<Object>} Query result
 */
export const queryContract = async (contractAddr, CONTRACT_MESSAGE, endpt) => {
  try {
    const querier = await NibiruQuerier.connect(endpt);
    const res = await querier.nibiruExtensions.wasm.queryContractSmart(
      contractAddr,
      CONTRACT_MESSAGE
    );
    return res;
  } catch (error) {
    console.log(error);
  }
};

/**
 * Query balance for an account
 * @param {string} account - Account address
 * @param {string} denom - Token denomination
 * @param {string} endpt - Endpoint
 * @returns {Promise<Object>} Balance information
 */
export const queryBalance = async (account, denom, endpt) => {
  if (!account) {
    throw new Error("Account address is required");
  }
  if (!denom) {
    throw new Error("Denom is required for balance query");
  }
  if (!endpt) {
    throw new Error("Endpoint is required for balance query");
  }
  
  try {
    const querier = await NibiruQuerier.connect(endpt);
    const balance = await querier.getBalance(account, denom);
    
    // Handle case where balance might be null or undefined
    if (!balance) {
      console.warn("Balance query returned null/undefined for account:", account, "denom:", denom);
      return { amount: "0", denom: denom };
    }
    
    return balance;
  } catch (error) {
    console.error("Error in queryBalance:", error);
    console.error("Query params:", { account, denom, endpt });
    throw error;
  }
};

const perpPriceCache = {
  data: null,
  timestamp: 0,
};

/**
 * Get perpetual prices with caching
 * @param {number} marketIndex - Market index
 * @returns {Promise<Object>} Price data
 */
export const getPerpPrices = async (marketIndex) => {
  try {
    const now = Date.now();
    const CACHE_TTL = process.env.REACT_APP_PRICE_CACHE_TTL || 60000; // Default 1 minute

    // Return cached data if it's fresh
    if (perpPriceCache.data && now - perpPriceCache.timestamp < CACHE_TTL) {
      console.log("Using cached perp prices");
      return perpPriceCache.data;
    }

    console.log("Fetching fresh perp prices");
    const message = {
      get_perp_prices: {
        market_index: marketIndex,
        collateral_index: process.env.REACT_APP_PERP_TOKEN_INDEXES_COLLATERAL,
      },
    };

    const result = await queryContract(
      getContractAddresses().perp,
      message,
      nibiru.endptTm
    );

    if (result?.market) {
      console.log("Perp prices response:", result);
      const priceData = {
        basePrice: result.market.base_price_usd,
        basePerQuote: result.market.base_per_quote,
        collateralPrice: result.collateral,
        lastOracleUpdate: result.market.last_oracle_addr,
      };

      // Update cache
      perpPriceCache.data = priceData;
      perpPriceCache.timestamp = now;

      return priceData;
    }

    // If we have cache, return it even if expired - better than nothing
    if (perpPriceCache.data) {
      console.log("Contract query failed, using expired cache");
      return perpPriceCache.data;
    }

    return null;
  } catch (err) {
    console.error("Failed to get perp prices:", err);

    // Return cache on error if available
    if (perpPriceCache.data) {
      console.log("Error fetching prices, using cached data");
      return perpPriceCache.data;
    }

    return null;
  }
};

/**
 * Get current price from oracle
 * @returns {Promise<string|null>} Current price
 */
export const getCurrentPrice = async () => {
  try {
    const message = {
      get_exchange_rate: {
        base: 4,
        quote: 5,
      },
    };

    const result = await queryContract(
      process.env.REACT_APP_ORACLE_CONTRACT,
      message,
      nibiru.endptTm
    );

    return result?.base_price_usd || null;
  } catch (err) {
    console.error("Failed to fetch current price from oracle:", err);
    return null;
  }
};

/**
 * UNIVERSAL ADDRESS HANDLER - Converts EVM address to bech32 if needed
 * @param {string} account - Original account address (EVM or bech32)  
 * @param {string} walletType - Wallet type from useWalletConnection hook
 * @returns {Promise<string>} - Bech32 address for querying Cosmos contracts
 */
export const getQueryableAddress = async (account, walletType) => {
  try {
    console.log("getQueryableAddress called with:", { account, walletType });
    
    if (!account) {
      console.warn("No account provided to getQueryableAddress");
      return null;
    }

    // Check if this is an EVM address
    const isEvmAddress = account.startsWith('0x');
    
    // Check if wallet type suggests EVM (even if null, we can detect by address format)
    const isEvmWallet = walletType === 'metamask' || walletType === 'bybitWallet' || isEvmAddress;
    
    console.log("Address analysis:", { 
      isEvmAddress, 
      isEvmWallet, 
      account, 
      walletType: walletType || 'auto-detected'
    });

    if (isEvmAddress) {
      console.log("EVM address detected - Converting to bech32...");
      const bech32Address = await evmToBech32Address(account);
      console.log("Address converted:", { 
        original: account, 
        bech32: bech32Address 
      });
      return bech32Address;
    } else if (account.startsWith('nibi')) {
      // Already a bech32 address (Cosmos wallets)
      console.log("Cosmos bech32 address - Using directly:", account);
      return account;
    } else {
      console.warn("Unknown address format, using as-is:", account);
      return account;
    }
  } catch (error) {
    console.error("Failed to get queryable address:", error);
    console.error("Error details:", {
      message: error.message,
      account,
      walletType
    });
    // Return original address as fallback
    return account;
  }
};

/**
 * Fetches all trades for a given account (handles EVM conversion automatically)
 * @param {string} account - Account address (EVM or bech32)
 * @param {string} walletType - Wallet type from useWalletConnection hook
 * @returns {Promise<Array>} Array of trades
 */
export const getTrades = async (account, walletType = null) => {
  try {
    if (!account) {
      console.warn("No account provided to getTrades");
      return [];
    }

    // Get the queryable address (converts EVM to bech32 if needed)
    const queryAddress = await getQueryableAddress(account, walletType);
    
    if (!queryAddress) {
      console.error("Failed to get valid query address");
      return [];
    }

    // NOTE: the perp contract's `get_trades` variant takes Vec<(Addr, u64)> (specific
    // trade tuples), NOT { trader }. To list all of a user's positions/orders we must
    // use `list_trades_for_user`, otherwise the query errors ("Invalid type") and the
    // catch below silently returns [] → "No active trades" even with open positions.
    //
    // Flags fetch EVERYTHING the trade table needs:
    //   show_conditional: true → include limit/stop order records (Orders tab)
    //   show_closed: true      → include closed records (History tab)
    //
    // The contract caps scan_limit at 100 trade *indices* per call, so a user with
    // 1000+ trades needs pagination. With both flags on, every trade index yields a
    // record, so we walk the `start_idx` cursor down (latest → oldest) 100 at a time
    // until we reach index 0 (or an empty page). MAX_PAGES is a safety backstop.
    const PAGE = 100;
    const MAX_PAGES = 200; // backstop: up to 20,000 trades
    const parseIdx = (v) => {
      const n = parseInt(String(v).replace(/\D/g, ""), 10);
      return Number.isNaN(n) ? null : n;
    };

    const perpAddr = getContractAddresses().perp;
    let all = [];
    let startIdx; // undefined on first call → starts from the latest trade index

    for (let page = 0; page < MAX_PAGES; page++) {
      const message = {
        list_trades_for_user: {
          trader: queryAddress,
          show_closed: true,
          show_conditional: true,
          scan_limit: PAGE,
          ...(startIdx !== undefined ? { start_idx: startIdx } : {}),
        },
      };

      const result = await queryContract(perpAddr, message, nibiru.endptTm);
      const batch = Array.isArray(result)
        ? result
        : result && Array.isArray(result.data)
        ? result.data
        : [];

      if (batch.length === 0) break;
      all = all.concat(batch);

      // Next page starts just below the lowest index we just scanned.
      const indices = batch.map((t) => parseIdx(t.user_trade_index)).filter((n) => n !== null);
      const lowest = indices.length ? Math.min(...indices) : 0;
      if (lowest <= 0) break; // reached the first-ever trade
      startIdx = lowest - 1;
    }

    return all;
  } catch (err) {
    console.error("Failed to fetch trades:", err);
    console.error("Error details:", {
      message: err.message,
      stack: err.stack,
      account,
      walletType
    });
    return [];
  }
};

/**
 * Fetches trade infos for a given account, keyed by user_trade_index.
 *
 * The contract's `get_trade_infos` returns at most 100 infos per call, in
 * DESCENDING index order (start_idx down to start_idx - scan_limit + 1), and
 * TradeInfo carries no index of its own. Indexing the raw array by
 * user_trade_index (as this used to) pairs closed trades with some other
 * trade's closed_price, so the history table showed nonsense exit prices and
 * PnL. We page with an explicit start_idx and key each info by its position.
 *
 * @param {string} account - Account address (EVM or bech32)
 * @param {string} walletType - Wallet type from useWalletConnection hook
 * @param {number} latestIdx - Highest user_trade_index to fetch (from list_trades_for_user)
 * @returns {Promise<Map<number, Object>>} user_trade_index -> TradeInfo
 */
export const getTradeInfos = async (account, walletType = null, latestIdx = null) => {
  const byIndex = new Map();
  try {
    if (!account || latestIdx === null || latestIdx < 0) return byIndex;

    // Get the queryable address (converts EVM to bech32 if needed)
    const queryAddress = await getQueryableAddress(account, walletType);
    if (!queryAddress) {
      console.error("Failed to get valid query address");
      return byIndex;
    }

    const PAGE = 100; // contract max scan_limit
    const perpAddr = getContractAddresses().perp;

    for (let start = latestIdx; start >= 0; start -= PAGE) {
      const result = await queryContract(
        perpAddr,
        { get_trade_infos: { trader: queryAddress, scan_limit: PAGE, start_idx: start } },
        nibiru.endptTm
      );
      const page = Array.isArray(result)
        ? result
        : result && Array.isArray(result.data)
        ? result.data
        : [];

      // Every trade index gets a TradeInfo when the trade is created, so a page
      // is dense. If it is not, positions can't be trusted — skip the page
      // rather than attach the wrong close price to a trade.
      const expected = start - Math.max(0, start - PAGE + 1) + 1;
      if (page.length !== expected) {
        console.warn(`get_trade_infos page at ${start} returned ${page.length}/${expected} infos; skipping`);
        continue;
      }
      page.forEach((info, i) => byIndex.set(start - i, info));
    }
    return byIndex;
  } catch (err) {
    console.error("Failed to fetch trade infos:", err);
    return byIndex;
  }
};

// ── Trade fetching: instant repeat opens via a per-account cache ──
//
// The trade table opened slowly because it re-scanned ALL of a user's trades
// (paginated list + full get_trade_infos) on every tab open / city switch / price
// tick. Closed trades never change, so re-scanning them is wasted work.
//
// Strategy, per account:
//   • A FULL scan (paginated list + infos) runs on first load, after a trade
//     action (opts.forceClosed), or after a safety TTL. It caches the CLOSED
//     history (thousands of static rows) and the set of OPEN trade indices.
//   • Between full scans, a cheap refresh re-fetches only the known OPEN indices
//     via one batch get_trades call (fresh liquidation price / status) and reuses
//     the cached closed history. Open positions can sit at ANY index, so we learn
//     their indices from a full scan rather than only scanning the latest.

const _tradesCache = new Map(); // accountKey -> { queryAddress, openIndices, closed, ts }
const TRADES_CACHE_TTL_MS = 120000; // 2 min safety TTL (trade actions force-refresh sooner)

// Persist the cache across hard reloads so the tables paint instantly (F5 wipes
// the in-memory Map otherwise, forcing a full paginated re-scan before anything
// renders). Closed trades are immutable, so serving them from storage is safe.
// v2: v1 entries hold trade_infos paired with the wrong trades (see getTradeInfos).
const _TRADES_STORAGE_PREFIX = "ce_trades_v2_";
const _MAX_PERSIST_CLOSED = 400; // bound storage size; older rows fill in after revalidation
const _revalidating = new Set(); // accountKeys with an in-flight background scan

const _persistCache = (key, entry) => {
  try {
    localStorage.setItem(
      _TRADES_STORAGE_PREFIX + key,
      JSON.stringify({
        queryAddress: entry.queryAddress,
        openIndices: entry.openIndices,
        closed: entry.closed.slice(0, _MAX_PERSIST_CLOSED), // newest-first (scan walks latest→oldest)
        ts: entry.ts,
      })
    );
  } catch (_) {
    /* quota exceeded / serialization / SSR — non-fatal, cache stays in-memory only */
  }
};

const _hydrateCache = (key) => {
  try {
    const raw = localStorage.getItem(_TRADES_STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.closed) || !parsed.queryAddress) return null;
    parsed.openIndices = Array.isArray(parsed.openIndices) ? parsed.openIndices : [];
    return parsed;
  } catch (_) {
    return null;
  }
};

const _parseTradeIndex = (v) => {
  const n = parseInt(String(v).replace(/\D/g, ""), 10);
  return Number.isNaN(n) ? null : n;
};

/** True if the open-index set or closed-history size differs between two cache entries. */
const _cacheChanged = (a, b) => {
  if (!a || !b) return true;
  if (a.closed.length !== b.closed.length) return true;
  if (a.openIndices.length !== b.openIndices.length) return true;
  return a.openIndices.some((idx, i) => idx !== b.openIndices[i]);
};

/** Invalidate the cached trade data for an account (or all accounts). */
export const invalidateClosedTradesCache = (account) => {
  if (account) {
    const key = String(account).toLowerCase();
    _tradesCache.delete(key);
    try { localStorage.removeItem(_TRADES_STORAGE_PREFIX + key); } catch (_) {}
  } else {
    _tradesCache.clear();
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(_TRADES_STORAGE_PREFIX))
        .forEach((k) => localStorage.removeItem(k));
    } catch (_) {}
  }
};

/**
 * Enrich specific trade indices with full data (trade + trade_info + liquidation
 * price) via batch get_trades (chunked at 100). Used for the live OPEN set.
 */
const _enrichTradeIndices = async (queryAddress, indices) => {
  if (!indices || indices.length === 0) return [];
  const perpAddr = getContractAddresses().perp;
  const out = [];
  for (let i = 0; i < indices.length; i += 100) {
    const chunk = indices.slice(i, i + 100).map((idx) => [queryAddress, idx]);
    const res = await queryContract(perpAddr, { get_trades: chunk }, nibiru.endptTm);
    const arr = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);
    for (const td of arr) {
      if (!td || !td.trade) continue;
      out.push({
        ...td.trade,
        // liquidation_price sits at the top level of TradeData; fold it into
        // trade_info so the table (which reads trade_info.liquidation_price) sees it.
        trade_info: { ...(td.trade_info || {}), liquidation_price: td.liquidation_price },
      });
    }
  }
  return out;
};

/** Full scan: paginate all trades + infos, cache closed + open indices, return all. */
const _fullScanTrades = async (account, walletType) => {
  const key = String(account).toLowerCase();
  const queryAddress = await getQueryableAddress(account, walletType);
  if (!queryAddress) return [];

  // paginated list_trades_for_user (show_closed: true)
  const allRaw = await getTrades(account, walletType);
  const all = Array.isArray(allRaw) ? allRaw : [];

  const indices = all.map((t) => _parseTradeIndex(t?.user_trade_index)).filter((i) => i !== null);
  const latestIdx = indices.length ? Math.max(...indices) : null;
  const infos = await getTradeInfos(account, walletType, latestIdx);

  const closed = all
    .filter((t) => t && t.is_open === false)
    .map((t) => {
      const idx = _parseTradeIndex(t.user_trade_index);
      return { ...t, trade_info: (idx !== null && infos.get(idx)) || {} };
    });

  const openIndices = all
    .filter((t) => t && t.is_open === true)
    .map((t) => _parseTradeIndex(t.user_trade_index))
    .filter((i) => i !== null);

  const entry = { queryAddress, openIndices, closed, ts: Date.now() };
  _tradesCache.set(key, entry);
  _persistCache(key, entry);

  const open = await _enrichTradeIndices(queryAddress, openIndices);
  return [...open, ...closed];
};

/**
 * Fire-and-forget full re-scan used when we served a stale (reload-hydrated)
 * cache. When the fresh scan changes the open set or closed history, emit
 * `tradesRevalidated` so the tables re-render with reconciled data.
 */
const _revalidateInBackground = (account, walletType, key) => {
  if (_revalidating.has(key)) return;
  _revalidating.add(key);
  const before = _tradesCache.get(key);
  _fullScanTrades(account, walletType)
    .then(() => {
      const after = _tradesCache.get(key);
      if (_cacheChanged(before, after) && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("tradesRevalidated", { detail: { account } }));
      }
    })
    .catch(() => {})
    .finally(() => _revalidating.delete(key));
};

/**
 * Combined open + closed trades for the trade tables.
 *
 * Instant on repeat opens: the closed history and open-index set come from a
 * per-account cache. Each call still re-fetches the (few) OPEN indices for fresh
 * liquidation price / status via a single batch query. A full re-scan only runs
 * on first load, when a trade action occurs (opts.forceClosed), or after the TTL.
 */
export const getTradesWithInfos = async (account, walletType = null, opts = {}) => {
  try {
    if (!account) return [];
    const key = String(account).toLowerCase();
    let cached = _tradesCache.get(key);

    // First access after a hard reload: rehydrate from localStorage so we can
    // paint immediately instead of blocking on a full paginated scan.
    if (!cached) {
      const stored = _hydrateCache(key);
      if (stored) {
        cached = stored;
        _tradesCache.set(key, stored);
      }
    }

    if (!opts.forceClosed && cached) {
      // Cheap path: OPEN positions are always re-fetched fresh (live liquidation
      // price / status); CLOSED history is immutable so it's reused from cache.
      const open = await _enrichTradeIndices(cached.queryAddress, cached.openIndices);
      // If the cache is stale (e.g. hydrated from a previous session), reconcile
      // the closed history in the background and notify the UI when it changes.
      if (Date.now() - cached.ts >= TRADES_CACHE_TTL_MS) {
        _revalidateInBackground(account, walletType, key);
      }
      return [...open, ...cached.closed];
    }

    return await _fullScanTrades(account, walletType);
  } catch (err) {
    console.error("getTradesWithInfos failed:", err);
    const cached = _tradesCache.get(String(account || "").toLowerCase());
    return cached ? [...cached.closed] : [];
  }
};

/**
 * Get Open Interest for a specific market and collateral pair
 * @param {number} marketIndex - Market index (should be a number, not string)
 * @param {number} collateralIndex - Collateral index (defaults to 3 based on your config)
 * @returns {Promise<Object>} Open Interest data
 */
export const getOpenInterest = async (marketIndex, collateralIndex = 3) => {
  try {
    const message = {
      get_borrowing_pair_oi: {
        collateral_index: collateralIndex,
        market_index: marketIndex
      }
    };

    console.log("Open Interest query message:", message);

    const result = await queryContract(
      getContractAddresses().perp,
      message,
      nibiru.endptTm
    );

    console.log("Open Interest result:", result);
    return result;
  } catch (error) {
    console.error("Failed to get open interest:", error);
    throw new Error(`Open Interest not available for market ${marketIndex} with collateral ${collateralIndex}`);
  }
};

/**
 * Get 1% Market Depth for price impact calculations
 * @param {number} marketIndex - Market index
 * @returns {Promise<Object>} Market depth data
 */
export const getMarketDepth = async (marketIndex) => {
  try {
    const message = {
      get_pair_depth: {
        index: marketIndex
      }
    };

    console.log("Market Depth query message:", message);

    const result = await queryContract(
      getContractAddresses().perp,
      message,
      nibiru.endptTm
    );

    console.log("Market Depth result:", result);
    return result;
  } catch (error) {
    console.error("Failed to get market depth:", error);
    throw new Error(`Market depth not available for market ${marketIndex}`);
  }
};

/**
 * Get all prices for a market (including collateral price for USD conversion)
 * @param {number} marketIndex - Market index
 * @param {number} collateralIndex - Collateral index (defaults to 3)
 * @returns {Promise<Object>} Price data
 */
export const getPerpPricesForImpact = async (marketIndex, collateralIndex = 3) => {
  try {
    const message = {
      get_perp_prices: {
        market_index: `MarketIndex(${marketIndex})`,
        collateral_index: `TokenIndex(${collateralIndex})`
      }
    };

    console.log("Perp Prices query message:", message);

    const result = await queryContract(
      getContractAddresses().perp,
      message,
      nibiru.endptTm
    );

    console.log("Perp Prices for Impact result:", result);
    return result;
  } catch (error) {
    console.error("Failed to get perp prices for impact:", error);
    throw new Error(`Price data not available for market ${marketIndex}`);
  }
};

/**
 * Calculate price impact for a trade
 * @param {number} marketIndex - Market index
 * @param {number} collateralIndex - Collateral index
 * @param {number} tradeSize - Trade size in USD
 * @param {boolean} isLong - Whether the trade is long
 * @param {number} entryPrice - Entry price in USD
 * @returns {Promise<Object>} Price impact calculation result
 */
export const calculatePriceImpact = async (marketIndex, collateralIndex, tradeSize, isLong, entryPrice) => {
  try {
    console.log("Calculating price impact for:", { marketIndex, collateralIndex, tradeSize, isLong, entryPrice });

    // Check if we have valid inputs
    if (!marketIndex && marketIndex !== 0) {
      throw new Error("Market index is required");
    }
    if (!tradeSize || tradeSize <= 0) {
      throw new Error("Trade size must be greater than 0");
    }
    if (!entryPrice || entryPrice <= 0) {
      throw new Error("Entry price must be greater than 0");
    }

    // Fetch all required data in parallel
    const [openInterestData, marketDepthData, priceData] = await Promise.all([
      getOpenInterest(marketIndex, collateralIndex),
      getMarketDepth(marketIndex),
      getPerpPricesForImpact(marketIndex, collateralIndex)
    ]);

    // Validate all data was received
    if (!openInterestData) {
      throw new Error("Failed to fetch open interest data");
    }
    if (!marketDepthData) {
      throw new Error("Failed to fetch market depth data");
    }
    if (!priceData) {
      throw new Error("Failed to fetch price data");
    }

    // Extract and validate collateral price
    const collateralPrice = parseFloat(priceData.collateral);
    if (!collateralPrice || collateralPrice <= 0) {
      throw new Error("Invalid collateral price received");
    }

    // Extract and validate open interest values
    const longOI = parseFloat(openInterestData.long || "0");
    const shortOI = parseFloat(openInterestData.short || "0");
    
    // Convert Open Interest from token units to USD
    const openInterestUsd = (longOI + shortOI) * collateralPrice;

    // Calculate adjusted OI
    const adjustedOI = openInterestUsd + (tradeSize / 2);

    // Extract market depth based on position direction
    const depthAbove = parseFloat(marketDepthData.one_percent_depth_above_usd || "0");
    const depthBelow = parseFloat(marketDepthData.one_percent_depth_below_usd || "0");
    
    const depth = isLong ? depthAbove : depthBelow;

    if (depth === 0) {
      console.log("Market depth is zero - applying no price impact");
      const result = {
        impactUsd: 0,
        impactPercent: 0,
        finalPrice: entryPrice,
        openInterestUsd: openInterestUsd,
        adjustedOI: adjustedOI,
        depth: depth,
        collateralPrice: collateralPrice
      };

      console.log("Price impact calculation result (zero depth):", result);

      return {
        success: true,
        data: result
      };
    }

    // Calculate price impact percentage (as decimal, not percentage)
    const impactPercent = adjustedOI / depth / 100;

    // Calculate price impact in USD
    const priceImpact = entryPrice * impactPercent;

    // Calculate final execution price
    const finalPrice = isLong ? entryPrice + priceImpact : entryPrice - priceImpact;

    const result = {
      impactUsd: priceImpact,
      impactPercent: impactPercent * 100, // Convert to percentage for display
      finalPrice: finalPrice,
      openInterestUsd: openInterestUsd,
      adjustedOI: adjustedOI,
      depth: depth,
      collateralPrice: collateralPrice
    };

    console.log("Price impact calculation result:", result);

    return {
      success: true,
      data: result
    };

  } catch (error) {
    console.error("Price impact calculation failed:", error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Calculate liquidation price for a specific trade, matching the contract's
 * get_trade_liquidation_price_with_fees (without accrued borrowing fees —
 * use the keeper/contract `liquidation_price` for an open trade).
 * @param {Object} trade - Trade object with collateral_amount, leverage, open_price, long
 * @returns {number} Liquidation price
 */
export const calculateTradeLiquidationPrice = (trade) => {
  try {
    if (!trade || !trade.collateral_amount || !trade.leverage || !trade.open_price) {
      return 0;
    }

    const LIQ_THRESHOLD_P = 0.9;
    // 5% liquidation closing fee + 5% trigger fee, on collateral
    const LIQ_FEES_P = 0.1;

    const numericPrice = Number(trade.open_price);
    const numericLeverage = Number(trade.leverage);
    const numericCollateral = Number(trade.collateral_amount) / 1000000; // Convert from micro-USDC to USDC
    const isLong = trade.long;

    const remainingCollateral = (LIQ_THRESHOLD_P - LIQ_FEES_P) * numericCollateral;
    const liqPriceDistance = (remainingCollateral * numericPrice) /
                            (numericCollateral * numericLeverage);

    const liquidationPrice = isLong 
      ? numericPrice - liqPriceDistance
      : numericPrice + liqPriceDistance;

    return Math.max(0, liquidationPrice); // Ensure non-negative price
  } catch (error) {
    console.error("Failed to calculate liquidation price:", error);
    return 0;
  }
};